import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { formatRM, type Category, type Product } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { BigButton } from '../../components/BigButton.js';
import { StockAdjustDialog } from '../../components/admin/StockAdjustDialog.js';
import { Page, PageHeader } from '../../components/layout.js';

export function ProductsListPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restockProduct, setRestockProduct] = useState<Product | null>(null);

  const load = useCallback(async (q: string, catId: number | '', inactive: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        api.listProducts({
          q: q.trim().length >= 1 ? q.trim() : undefined,
          categoryId: catId === '' ? undefined : catId,
          includeInactive: inactive,
        }),
        api.listCategories(),
      ]);
      setProducts(productsRes.products);
      setCategories(categoriesRes.categories);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(search, categoryId, includeInactive);
  }, [search, categoryId, includeInactive, load]);

  function handleAdjusted(updated: Product) {
    setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setRestockProduct(null);
  }

  return (
    <Page>
      <PageHeader
        title="Products"
        actions={
          <>
            <a href="/api/export/inventory.csv">
              <BigButton variant="outline">Export CSV</BigButton>
            </a>
            <Link to="/admin/products/new">
              <BigButton>Add product</BigButton>
            </Link>
          </>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <label htmlFor="product-search" className="sr-only">
          Search by code or name
        </label>
        <input
          id="product-search"
          type="search"
          placeholder="Search by code or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input flex-1 min-w-[16rem]"
        />
        <label htmlFor="category-filter" className="sr-only">
          Filter by category
        </label>
        <select
          id="category-filter"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
          className="input sm:w-56"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-3 font-body text-base text-text min-h-12 px-1 whitespace-nowrap">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-6 w-6 min-h-0 accent-teal shrink-0"
          />
          Include inactive
        </label>
      </div>

      {loading && products.length === 0 ? (
        <Spinner label="Loading products" />
      ) : error ? (
        <EmptyState title="Could not load products" description={error} />
      ) : products.length === 0 ? (
        <EmptyState title="No products found" description="Try a different search or add a new product." />
      ) : (
        <ul className="list-stack">
          {products.map((product) => (
            <li key={product.id} className="card-row flex items-center gap-3">
              <Link to={`/admin/products/${product.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-14 w-14 shrink-0 rounded-lg bg-teal-dark flex items-center justify-center overflow-hidden">
                  {product.thumbUrl ? (
                    <img src={product.thumbUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    // `truncate` (not a hard overflow-hidden clip) so a long
                    // code ellipsizes on one line instead of getting cut off
                    // top/bottom+sides mid-character - see QA report P3 #13.
                    // The full code is always shown, readable, just below.
                    <span className="block w-full max-w-full truncate text-white font-heading font-bold text-sm px-1 text-center">
                      {product.code}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-body font-semibold text-base text-text truncate">{product.name}</p>
                  <p className="font-body text-sm text-muted">{product.code}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-heading font-semibold text-base text-ink">
                      {formatRM(product.priceCents)}
                    </span>
                    {!product.isActive ? (
                      <span className="rounded-full bg-gray-200 text-muted font-body font-bold text-sm px-2 py-0.5">
                        Inactive
                      </span>
                    ) : product.lowStock ? (
                      <span className="rounded-full bg-orange text-ink font-body font-bold text-sm px-2 py-0.5">
                        Low: {product.stockQty}
                      </span>
                    ) : (
                      <span className="font-body text-sm text-muted">{product.stockQty} in stock</span>
                    )}
                  </div>
                </div>
              </Link>
              <button
                type="button"
                onClick={() => setRestockProduct(product)}
                className={clsx(
                  'shrink-0 min-h-12 px-3 rounded-lg font-heading font-semibold text-sm bg-aqua text-teal-dark',
                )}
              >
                Restock
              </button>
            </li>
          ))}
        </ul>
      )}

      {restockProduct && (
        <StockAdjustDialog
          product={restockProduct}
          onClose={() => setRestockProduct(null)}
          onAdjusted={handleAdjusted}
        />
      )}
    </Page>
  );
}
